/**
 * GoalsHero — the dashboard's lead section.
 *
 * Goals are the product's center of gravity (target → discovered drivers →
 * best model → live forecasts), so the dashboard leads with them:
 *
 *   * No goals   — a single large call-to-action to create the first goal.
 *   * With goals — a card grid: one card per goal with its serving state,
 *     champion quality (accuracy + skill), driver count and horizon, plus
 *     a dashed "create" tile and a "view all" affordance.
 *
 * Data comes from ``summary.goals`` (rich: includes champion accuracy /
 * skill) with a graceful fallback to the ``discovery_goals`` redux list
 * (same cards, metrics omitted) so the section renders even against an
 * older backend.
 */

import React from 'react';
import {
  Box, Chip, Skeleton, Stack, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { GoalsIcon } from '../../global/CustomIcons';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import CableIcon from '@mui/icons-material/Cable';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { Surface, StatusDot } from './DashboardPrimitives';
import { CMButton } from '../../../containers/styledComponents';
import AccuracyInfoTooltip from '../../common/AccuracyInfoTooltip';

const GOAL_TYPE_LABELS = {
  forecast_value: 'Forecast value',
  forecast_distribution: 'Forecast distribution',
  trend: 'Trend',
  anomaly: 'Anomaly detection',
  rank_by_risk: 'Fleet · rank by risk',
  rank_by_growth: 'Fleet · rank by growth',
};

// Goal lifecycle status -> visual tone (``live`` = legacy alias of serving).
const STATUS_META = {
  serving: { tone: 'success', label: 'Serving' },
  live:    { tone: 'success', label: 'Serving' },
  running: { tone: 'primary', label: 'Running' },
  failed:  { tone: 'error',   label: 'Failed' },
  idle:    { tone: 'neutral', label: 'Idle' },
};

const isServing = (s) => s === 'serving' || s === 'live';

const fmtPct = (v) => (
  typeof v === 'number' && !Number.isNaN(v) ? `${v.toFixed(1)}%` : null
);

const horizonLabel = (bins, secs) => {
  if (!bins || !secs) return null;
  const total = bins * secs;
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return `${hours}h`;
  return `${Math.floor(total / 60)}m`;
};

const nextForecastLabel = (iso) => {
  try {
    if (!iso) return null;
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return null;
    const ms = dt.getTime() - Date.now();
    if (ms <= 60_000) return 'due now';
    const m = Math.round(ms / 60_000);
    if (m < 60) return `in ${m}m`;
    const h = Math.round(m / 60);
    if (h < 48) return `in ${h}h`;
    return `in ${Math.round(h / 24)}d`;
  } catch (_e) { return null; }
};

/* Compact metric block used inside a goal card (value over tiny label). */
const MiniStat = ({ value, label, color = 'text.primary', tip }) => {
  const inner = (
    <Box sx={{ minWidth: 64 }}>
      <Typography sx={{ fontFamily: 'Lexend Variable, Arial', fontSize: 18, fontWeight: 700, lineHeight: 1.15, color }}>
        {value ?? '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, letterSpacing: 0.4 }}>
        {label}
      </Typography>
    </Box>
  );
  return tip ? <Tooltip arrow title={tip}>{inner}</Tooltip> : inner;
};

const GoalCard = ({ goal, navigate }) => {
  const theme = useTheme();
  const meta = STATUS_META[goal.status] || STATUS_META.idle;
  const serving = isServing(goal.status);
  const inShock = goal.shock_state && goal.shock_state !== 'normal';
  const isFleet = goal.goal_kind === 'fleet';

  const acc = fmtPct(goal.accuracy);
  const skill = fmtPct(goal.skill);
  const skillColor = goal.skill == null ? 'text.primary'
    : goal.skill <= 0 ? 'text.disabled'
      : goal.skill < 15 ? 'warning.main'
        : goal.skill < 40 ? 'info.main'
          : 'success.main';
  const horizon = horizonLabel(goal.horizon_bins, goal.interval_seconds);
  const nextAt = serving && goal.live_data !== false
    ? nextForecastLabel(goal.next_serve_at) : null;

  return (
    <Surface
      onClick={() => navigate(`/goals/${goal.goal_id}`)}
      sx={{
        p: 2.25,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        // Serving goals glow gently — they're the live ones.
        ...(serving && {
          borderColor: alpha(theme.palette.success.main, 0.35),
          boxShadow: `0 0 24px -12px ${alpha(theme.palette.success.main, 0.55)}`,
        }),
        '&:hover': {
          borderColor: alpha(
            serving ? theme.palette.success.main : theme.palette.primary.main,
            0.6,
          ),
        },
      }}
    >
      {/* name + status */}
      <Stack direction="row" spacing={1} alignItems="center">
        <StatusDot tone={meta.tone} size={9} />
        <Typography variant="body1" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {goal.name || goal.target_key}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={GOAL_TYPE_LABELS[goal.goal_type] || goal.goal_type || 'goal'}
          sx={{ height: 20, fontSize: 10, flexShrink: 0 }}
        />
      </Stack>

      {/* target key (when it differs from the display name) */}
      {goal.name && goal.target_key && goal.name !== goal.target_key && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: -0.75 }}>
          {goal.target_key}
        </Typography>
      )}

      {/* champion quality + shape (fleets lead with population stats) */}
      <Stack direction="row" spacing={2.5} alignItems="flex-end">
        {isFleet ? (
          <>
            <MiniStat
              value={goal.member_count ?? '—'}
              label="MEMBERS"
              tip="Series scored and ranked on every fleet run."
            />
            <MiniStat
              value={goal.fleet_model || '—'}
              label="MODEL"
              tip="The forecaster that won the fleet's bake-off — every member is scored with it."
            />
          </>
        ) : (
          <>
            <AccuracyInfoTooltip
              value={typeof goal.accuracy === 'number' ? goal.accuracy : null}
              scoreKind={['trend', 'anomaly'].includes(goal.goal_type) ? 'classification' : undefined}
            >
              <Box sx={{ cursor: 'help' }}>
                <MiniStat value={acc} label="ACCURACY" />
              </Box>
            </AccuracyInfoTooltip>
            <MiniStat
              value={skill}
              label="SKILL"
              color={skillColor}
              tip="Forecast skill vs a naive persistence baseline — how much the model beats carrying the last value forward."
            />
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack spacing={0.25} alignItems="flex-end">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <BubbleChartIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {goal.drivers ?? 0} driver{(goal.drivers ?? 0) === 1 ? '' : 's'}
            </Typography>
          </Stack>
          {horizon && (
            <Typography variant="caption" color="text.secondary">
              {horizon} horizon
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* footer: state + shock + next forecast */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 'auto' }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: meta.tone === 'neutral' ? 'text.secondary' : `${meta.tone === 'primary' ? 'primary' : meta.tone}.main`,
          }}
        >
          {meta.label}
        </Typography>
        {!serving && !goal.champion_pipeline_id && !isFleet && (
          <Typography variant="caption" color="text.disabled">
            · no model yet
          </Typography>
        )}
        {inShock && (
          <Tooltip arrow title="Shock-mode serving is active — intervals widen until recovery.">
            <Chip
              size="small" color="warning" icon={<BoltIcon sx={{ fontSize: 12 }} />}
              label={goal.shock_state}
              sx={{ height: 18, fontSize: 10 }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {nextAt && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ScheduleIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">
              next forecast {nextAt}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Surface>
  );
};

/* Dashed "add another goal" tile that closes the grid. */
const CreateGoalTile = ({ navigate }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={() => navigate('/goals/create')}
      sx={{
        borderRadius: 2,
        border: '1.5px dashed',
        borderColor: alpha(theme.palette.primary.main, 0.35),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        minHeight: 150,
        cursor: 'pointer',
        color: 'primary.main',
        transition: 'border-color 160ms ease, background-color 160ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      <AddIcon sx={{ fontSize: 26 }} />
      <Typography variant="body2" fontWeight={600}>Create a goal</Typography>
    </Box>
  );
};

/* Full-width call-to-action when the workspace has no goals yet. Funnel-
   aware: with no data yet, a goal can't target anything — so the primary
   action becomes "connect data" and goal creation waits its turn. */
const NoGoalsCTA = ({ navigate, hasData }) => {
  const theme = useTheme();
  return (
    <Surface
      sx={{
        p: { xs: 3, md: 5 },
        textAlign: 'center',
        borderColor: alpha(theme.palette.primary.main, 0.35),
        boxShadow: `0 0 48px -16px ${alpha(theme.palette.primary.main, 0.5)}`,
      }}
    >
      <Box
        sx={{
          width: 56, height: 56, mx: 'auto', mb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1),
          color: 'primary.main',
        }}
      >
        {hasData ? <GoalsIcon sx={{ fontSize: 30 }} /> : <CableIcon sx={{ fontSize: 30 }} />}
      </Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        {hasData ? 'Create your first goal' : 'Connect your data first'}
      </Typography>
      <Typography
        variant="body2" color="text.secondary"
        sx={{ maxWidth: 560, mx: 'auto', mb: 2.5 }}
      >
        {hasData
          ? 'Goals are where the platform works for you. Pick a target metric and Predict.ai discovers its drivers, fits and deploys the best model, and serves live forecasts with an honest track record — automatically.'
          : 'Goals need a signal to target. Upload a file or connect a source, then create a goal — Predict.ai discovers its drivers, fits and deploys the best model, and serves live forecasts automatically.'}
      </Typography>
      {hasData ? (
        <CMButton startIcon={<AddIcon />} onClick={() => navigate('/goals/create')}>
          Create a goal
        </CMButton>
      ) : (
        <Stack spacing={1} alignItems="center">
          <CMButton startIcon={<CableIcon />} onClick={() => navigate('/datasources/connect')}>
            Connect data
          </CMButton>
          <Typography
            variant="caption"
            onClick={() => navigate('/goals/create')}
            sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
          >
            I already know what I'm doing — create a goal anyway
          </Typography>
        </Stack>
      )}
    </Surface>
  );
};

const GoalsHero = ({ goals, loading, navigate, hasData = true }) => {
  if (loading && goals == null) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
          gap: 2, mb: 2,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={160} sx={{ borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  const items = goals || [];
  if (items.length === 0) {
    return <Box sx={{ mb: 2 }}><NoGoalsCTA navigate={navigate} hasData={hasData} /></Box>;
  }

  const servingCount = items.filter((g) => isServing(g.status)).length;

  return (
    <Box sx={{ mb: 2 }}>
      {/* section header */}
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
        <GoalsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 700, color: 'text.secondary' }}>
          Goals
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {servingCount} of {items.length} serving live forecasts
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          onClick={() => navigate('/goals')}
          sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
        >
          View all goals
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {items.map((g) => (
          <GoalCard key={g.goal_id} goal={g} navigate={navigate} />
        ))}
        <CreateGoalTile navigate={navigate} />
      </Box>
    </Box>
  );
};

export default GoalsHero;
